import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../Services/api/config";

const ThemeContext = createContext(null);

const THEME_PRESETS = [
  { name: "Green", color: "#4ADE80" },
  { name: "Blue", color: "#60A5FA" },
  { name: "Purple", color: "#A78BFA" },
  { name: "Pink", color: "#F472B6" },
  { name: "Orange", color: "#FB923C" },
  { name: "Cyan", color: "#22D3EE" },
  { name: "Red", color: "#F87171" },
  { name: "Yellow", color: "#FACC15" },
];

const DEFAULT_ACCENT = "#4ADE80";

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
};

const applyAccent = (color) => {
  const root = document.documentElement;
  root.style.setProperty("--accent-color", color);
  root.style.setProperty("--accent-rgb", hexToRgb(color));
};

export const ThemeProvider = ({ children }) => {
  const [accentColor, setAccentColor] = useState(() => {
    return localStorage.getItem("accentColor") || DEFAULT_ACCENT;
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    applyAccent(accentColor);
  }, [accentColor]);

  const loadPreferences = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await api.get("/preferences");
      if (res.data?.accentColor) {
        setAccentColor(res.data.accentColor);
        localStorage.setItem("accentColor", res.data.accentColor);
      }
    } catch {
      // Preferences may not exist yet — use default
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const changeAccent = useCallback(async (color) => {
    setAccentColor(color);
    localStorage.setItem("accentColor", color);
    applyAccent(color);
    try {
      await api.patch("/preferences", { accentColor: color });
    } catch {
      // Best-effort save
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ accentColor, changeAccent, THEME_PRESETS, loaded }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { accentColor: DEFAULT_ACCENT, changeAccent: () => {}, THEME_PRESETS, loaded: true };
  return ctx;
};

export { THEME_PRESETS, DEFAULT_ACCENT };
